/**
 * Token Balance List Component
 *
 * v3 redesign — displays list of token balances with logos and formatted values.
 * Uses v3 typography tokens, foreground-neutral for labels, numeric font for values.
 *
 * This box is the reference layout for every sibling box in the account menu
 * (see `UsdvMigrationCard`): sunken card, `p-4`, a `body` /
 * foreground-neutral heading with `pb-2`, then a gapless column of `py-2` rows.
 */
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { Card, Skeleton, Text } from "@solomon-labs/design/components/ui";
import React, { useMemo } from "react";

import SOLOLogo from "../../assets/icons/SOLO.svg";
import USDvLogo from "../../assets/logos/usdv-logomark.png";
import type { TokenBalances } from "../../wallet/types/balances";
import { TOKEN_BALANCE_DECIMALS } from "../../wallet/utils/parseTokenBalances";

import type { TokenBalanceDisplay } from "./types";

interface TokenBalanceListProps {
    /** Token balances from wallet store */
    balances: TokenBalances;
    /** Whether balances are loading */
    isLoading: boolean;
    /** Whether balances are reconciling after a transaction */
    isReconciling?: boolean;
    /** The account is unverified or its balances could not be loaded. */
    unavailable?: boolean;
}

/**
 * Token configuration for display
 */
const TOKEN_CONFIG: Array<{
    key: keyof TokenBalances;
    label: string;
    logo: string;
}> = [
    { key: "usdv", label: "USDv", logo: USDvLogo },
    { key: "solo", label: "SOLO", logo: SOLOLogo }
];

/**
 * Loading skeleton for balance row.
 *
 * `h-10 md:h-10.5` is the measured height of a loaded row: `py-2` plus the
 * taller of the 24px logo and the `body` line box (22px, 26px from `md`), so
 * the list does not jump when balances arrive.
 */
function BalanceSkeleton(): React.ReactElement {
    return (
        <div className="flex h-10 items-center justify-between md:h-10.5">
            <div className="flex items-center gap-2">
                <Skeleton className="size-6" />
                <Skeleton className="h-3.5 w-10" />
            </div>
            <Skeleton className="h-3.5 w-14" />
        </div>
    );
}

/**
 * Single token balance row
 */
function TokenBalanceRow({
    logo,
    label,
    value,
    isUpdating
}: TokenBalanceDisplay & { isUpdating?: boolean }): React.ReactElement {
    return (
        <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
                {/* Decorative — the label beside it already names the token,
                 *  so the mark stays out of the a11y tree. */}
                <img src={logo} alt="" className="size-6" />
                <Text variant="body" className="text-foreground-muted">
                    {label}
                </Text>
            </div>
            {isUpdating ? (
                <Skeleton className="h-3.5 w-14" />
            ) : (
                <Text
                    variant="number"
                    className="text-foreground-base tabular-nums"
                >
                    {value}
                </Text>
            )}
        </div>
    );
}

/**
 * Token balance list component
 */
export function TokenBalanceList({
    balances,
    isLoading,
    isReconciling,
    unavailable = false
}: TokenBalanceListProps): React.ReactElement {
    const hasExistingBalances = Object.values(balances).some((b) => b !== null);
    const isUpdating = (isLoading || isReconciling) && hasExistingBalances;
    const showFullSkeleton = isLoading && !hasExistingBalances;

    const displayBalances = useMemo((): TokenBalanceDisplay[] => {
        return TOKEN_CONFIG.map(({ key, label, logo }) => {
            const balance = balances[key];
            // Token amounts, not dollar values: every decimal the mint has.
            const decimals = TOKEN_BALANCE_DECIMALS[key];
            const value = unavailable
                ? "—"
                : (balance ?? new BigDecimal(0)).toFixed(decimals);
            return { logo, label, value };
        });
    }, [balances, unavailable]);

    return (
        <Card
            surface="sunken"
            rounded={false}
            className="flex w-full flex-col p-4"
        >
            <Text variant="body" className="text-foreground-neutral pb-2">
                Balances
            </Text>
            <div
                className="flex flex-col"
                aria-busy={showFullSkeleton || isUpdating ? true : undefined}
            >
                {showFullSkeleton
                    ? TOKEN_CONFIG.map((_, index) => (
                          <BalanceSkeleton key={index} />
                      ))
                    : displayBalances.map((balance) => (
                          <TokenBalanceRow
                              key={balance.label}
                              {...balance}
                              isUpdating={isUpdating}
                          />
                      ))}
            </div>
        </Card>
    );
}
